import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit2, Trash2, Activity } from 'lucide-react';
import { Table } from '@/components';
import { courierApi } from '@/services/courierApi';
import { CourierListItem } from '@/types/courier';
import toast from 'react-hot-toast';

const formatDate = (value: unknown) => {
    if (!value) return '-';

    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? '-' : value.toLocaleDateString('tr-TR');
    }

    if (typeof value === 'object' && value !== null) {
        const timestamp = value as { _seconds?: number; seconds?: number };
        const seconds = timestamp._seconds ?? timestamp.seconds;

        if (typeof seconds === 'number') {
            return new Date(seconds * 1000).toLocaleDateString('tr-TR');
        }
    }

    const parsedDate = new Date(value as string | number | Date);
    return Number.isNaN(parsedDate.getTime()) ? '-' : parsedDate.toLocaleDateString('tr-TR');
};

type StatusFilter = 'all' | 'active' | 'inactive';

const STATUS_LABELS: Record<string, string> = {
    active: 'Aktif',
    inactive: 'İnaktif',
};

const STATUS_COLORS: Record<string, { badge: string; bg: string }> = {
    active: {
        badge: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
        bg: 'success',
    },
    inactive: {
        badge: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
        bg: 'danger',
    },
};

export const CouriersList: React.FC = () => {
    const navigate = useNavigate();
    const [couriers, setCouriers] = useState<CourierListItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
    const [offset, setOffset] = useState(0);
    const limit = 50;

    const loadCouriers = async () => {
        setLoading(true);
        try {
            const response = await courierApi.getAllCouriers({
                status: statusFilter !== 'all' ? statusFilter : undefined,
                limit,
                offset,
            });
            setCouriers(response.couriers || []);
        } catch (error) {
            console.error('Kuryeler yüklenemedi:', error);
            toast.error('Kuryeler yüklenemedi');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setOffset(0);
    }, [statusFilter]);

    useEffect(() => {
        loadCouriers();
    }, [statusFilter, offset]);

    const handleDelete = async (courierId: string) => {
        if (!confirm('Bu kuryeyi silmek istediğinizden emin misiniz?')) return;

        try {
            await courierApi.deleteCourier(courierId);
            toast.success('Kurye silindi');
            loadCouriers();
        } catch (error) {
            console.error('Silme hatası:', error);
            toast.error('Kurye silinemedi');
        }
    };

    const handleStatusChange = async (courierId: string, newStatus: string) => {
        try {
            await courierApi.updateCourierStatus(courierId, newStatus);
            toast.success('Durum güncellendi');
            loadCouriers();
        } catch (error) {
            console.error('Durum güncelleme hatası:', error);
            toast.error('Durum güncellenemedi');
        }
    };

    const tableData = couriers.map((courier) => ({
        id: courier.id,
        name: `${courier.firstName} ${courier.lastName}`,
        email: courier.email,
        phone: courier.phone,
        vendor: courier.vendorName || '-',
        deliveries: courier.totalDeliveries || 0,
        rating: `${(courier.rating || 0).toFixed(1)} ⭐`,
        status: STATUS_LABELS[courier.status] || courier.status,
        statusRaw: courier.status,
        date: formatDate(courier.createdAt),
    }));

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100">
                        Kuryeler
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400 mt-1">
                        Sistem kuryelerini yönetin
                    </p>
                </div>
                <button
                    onClick={() => navigate('/admin/couriers/create')}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                >
                    <Plus size={20} />
                    Kurye Ekle
                </button>
            </div>

            {/* Filters */}
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                <div className="flex gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Durum
                        </label>
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="all">Hepsi</option>
                            <option value="active">Aktif</option>
                            <option value="inactive">İnaktif</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                    <p className="text-sm text-gray-600 dark:text-gray-400">Toplam Kurye</p>
                    <p className="text-2xl font-bold text-gray-800 dark:text-gray-100 mt-1">
                        {couriers.length}
                    </p>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg shadow">
                    <p className="text-sm text-green-600 dark:text-green-400">Aktif Kuryeler</p>
                    <p className="text-2xl font-bold text-green-700 dark:text-green-300 mt-1">
                        {couriers.filter((c) => c.status === 'active').length}
                    </p>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg shadow">
                    <p className="text-sm text-blue-600 dark:text-blue-400">Toplam Teslimler</p>
                    <p className="text-2xl font-bold text-blue-700 dark:text-blue-300 mt-1">
                        {couriers.reduce((sum, c) => sum + (c.totalDeliveries || 0), 0)}
                    </p>
                </div>
                <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg shadow">
                    <p className="text-sm text-purple-600 dark:text-purple-400">Ort. Rating</p>
                    <p className="text-2xl font-bold text-purple-700 dark:text-purple-300 mt-1">
                        {(
                            couriers.reduce((sum, c) => sum + (c.rating || 0), 0) /
                            (couriers.length || 1)
                        ).toFixed(1)}{' '}
                        ⭐
                    </p>
                </div>
            </div>

            {/* Table */}
            <Table
                headers={['Ad', 'E-posta', 'Telefon', 'Satıcı', 'Teslim', 'Rating', 'Durum', 'Oluşturma Tarihi']}
                data={tableData}
                renderCell={(item: any, header: string) => {
                    switch (header) {
                        case 'Ad':
                            return item.name;
                        case 'E-posta':
                            return item.email;
                        case 'Telefon':
                            return item.phone;
                        case 'Satıcı':
                            return item.vendor;
                        case 'Teslim':
                            return item.deliveries;
                        case 'Rating':
                            return item.rating;
                        case 'Durum':
                            return (
                                <span
                                    className={`px-3 py-1 rounded-full text-sm font-medium ${
                                        STATUS_COLORS[item.statusRaw]?.badge || 'bg-gray-100'
                                    }`}
                                >
                                    {item.status}
                                </span>
                            );
                        case 'Oluşturma Tarihi':
                            return item.date;
                        default:
                            return '-';
                    }
                }}
                actions={(row: any) => (
                    <div className="flex gap-2">
                        <button
                            onClick={() => navigate(`/admin/couriers/${row.id}`)}
                            className="p-1 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition"
                            title="Detayları görüntüle"
                        >
                            <Edit2 size={18} />
                        </button>
                        <button
                            onClick={() => handleDelete(row.id)}
                            className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition"
                            title="Sil"
                        >
                            <Trash2 size={18} />
                        </button>
                    </div>
                )}
            />
        </div>
    );
};

export default CouriersList;
